// What HOME's Follow-ups strip lists (busy-my-web#295; Figma 40009470-209128).
//
// ONE email row and three Ongoing counts, four lines at most: "Emails need attention" — the way
// into the full Email Briefing — then "3 ongoings need your attention", "2 ongoings overdue",
// "5 ongoings waiting on someone", ranked, zero counts dropped.
//
// Pouria, 2026-08-29: "we dont need follow ups like this it shoud be 4 and emails need attentions
// and some thing like total emails today". What home drew instead, after batch 12, was four
// per-thread OFFERS — "This thread may relate to <Ongoing>" with Add / View Ongoing / Not related,
// once per thread — which filled all four lines with one question asked four times and left no room
// for the counts. The offers themselves are not gone; they are asked where the thread or the story
// is (the thread view's "Suggested:", the Ongoing story block), which is where an answer can be
// judged. Home is the summary again.
//
// Design clarification, 2026-09-02 (Figma 40009470-209128): home carries NO individual email
// counts. The counts — 124 emails · 9 require action · 6 waiting for replies · 27 orders ·
// 20 receipts · 13 subscriptions · 4 email groups — are the Briefing Email Inbox's, drawn once, in
// the Briefing (lib/briefings/email.js), and that one view is what home's row opens: the same
// dashboard whether you arrive from home or from the email module. So the "9 emails need attention"
// and "124 emails today" rows #295 put here are gone again, and one row stands in their place.
//
// …and it opens IN PLACE (coordinator, 2026-09-02, second fix): the row carries `modal`, not a
// `route`. Pushing /emails/workspace took the user off home — the middle became the workspace and
// the strip went with it. The Briefing is drawn in an overlay now (components/Home/
// EmailBriefingModal.vue; the rule in lib/emailBriefingModal.js), home stays behind it, and
// closing the overlay is closing it — no navigation either way.
//
// …and the other three rows are not about mail at all (busy-my-web#336, Saffet 2026-09-03: "I
// should only see Emails need attention as a Follow-up, the other three should not be email related
// only a global Follow-up concerning your ongoings"). What stood there — the BFF roll-up's
// "10 overdue" / "5 waiting on someone" / "356 junk emails to delete" (`GET v1/timeline/follow-ups`)
// — counted THREADS: emails and the tasks living on them. Home now counts ONGOINGS instead, off the
// list the sidebar already draws (`GET v1/ongoing`, `ongoing/items`), by the server's own status
// ladder (busy-timeline-srv#27): needs attention, overdue, waiting.
//
// The ladder is the only Ongoing-wide state the list payload carries (OngoingResponse: id, title,
// origin, status, priorityScore, …) — there are no per-ongoing expected dates on it, so there is no
// honest "overdue waiting" row to draw here and none is invented. It is not needed either: the
// server puts a wait that ran late on the `needsAttention` rung and anything genuinely late on
// `overdue`, so the two top rows already say it, computed on one clock.
//
// Gone with the roll-up: the "Possible relations" line ("N emails may belong to an Ongoing",
// lib/emailRelationOffers.js). It is Ongoing-adjacent, but it is worded — and read — as an EMAIL
// count, which is exactly the second email row this issue removes. The offers themselves are
// untouched and one click away: the Briefing this page's email row opens carries the banner, and
// each Ongoing's story block still asks about its own.
//
// Pure — no store, no api, nothing mounted. The component supplies the Briefing's counts
// (`GET v1/email/briefing`) and the ongoing list; this file decides which candidates exist, in what
// order, and which four survive.

import { EMAIL_BRIEFING_MODAL } from '@/lib/emailBriefingModal'

/** The strip never draws more than this, in any population path. */
export const MAX_ROWS = 4

export const EMAIL_BRIEFING_SECTION = 'email-briefing'

// ── READING A COUNT (busy-my-web#531) ─────────────────────────────────────────────────────────
//
// Saffet, 2026-09-17: "I cannot see any Follow-ups in the home page — no emails need attention."
//
// The strip is drawn from two independent answers — `GET v1/email/briefing` (the mailbox's counts)
// and the Ongoing list plus the open waits — and the ways a row can vanish for a reason nobody can
// see are all in the READING of them, not in the rules above:
//
//   • a FIELD RENAMED on either side of busy-main-bff#38/#41 (`emailsTotal` → `totalEmails`,
//     `requireAttention` → `requiresAttention`, a `total` where a `count` was) — `Number(undefined)`
//     is NaN, `|| 0` turns that into a zero, and a zero is indistinguishable from an empty mailbox;
//   • a count arriving as a STRING ("124", or "1,240" from a formatter) — the first reads fine
//     through Number(), the second is NaN and therefore, again, a silent zero;
//   • one source THROWING or answering a shape nobody expected, which before this issue could take
//     the whole strip with it: every row was built inside one expression.
//
// So a count is read by NAME-OR-ALIAS and coerced, and each source contributes its rows on its own
// (see `homeFollowUpRows`). None of this invents a number: an absent count is still nothing, and a
// mailbox with no mail still draws no mail row. It only stops a rename or a comma from being read
// as "nothing is happening".

/** The names `GET v1/email/briefing` has used, or could reasonably use, for the mailbox total. */
export const TOTAL_FIELDS = ['emailsTotal', 'totalEmails', 'emails', 'total', 'count']

/** …and for the ones that want an answer. */
export const ATTENTION_FIELDS = ['requireAttention', 'requiresAttention', 'requireAction', 'needsAttention', 'attention']

/**
 * One count off a payload, under the first of `names` that carries a readable number.
 *
 * Strings are read (a JSON number that came back quoted is still that number) with spaces and
 * thousands separators taken out first; anything that is not a finite number — null, '', 'lots',
 * NaN, an object — is skipped, and the NEXT alias is tried, which is what makes a rename survivable
 * rather than a zero. Negative counts are passed through as they arrived; the row rules above are
 * what decide that a count of zero or less is not a row.
 */
export function countValue(source, ...names) {
  if (!source || typeof source !== 'object' || Array.isArray(source)) return 0
  for (const name of names) {
    const raw = source[name]
    if (raw === null || raw === undefined || raw === '' || typeof raw === 'object') continue
    const n = typeof raw === 'string' ? Number(raw.replace(/[\s,]/g, '')) : Number(raw)
    if (Number.isFinite(n)) return n
  }
  return 0
}

/**
 * The words on the row: always "Emails need attention" (Pouria, 2026-09-15: "emails need attention
 * should be ALWAYS on follow-ups on home as soon as the user gives us permission to email … I can
 * see some 'Email briefing' — remove it"). The count beside it says how many; the row itself is the
 * standing way into the Briefing, not a claim about the number.
 */
export function emailBriefingLabel() {
  return 'Emails need attention'
}

/**
 * The email row, or none. Drawn the moment a mailbox is CONNECTED (`connected`, the integrations
 * store's Google flag) — before the Briefing's counts have even landed — and, for a mailbox whose
 * connection state is unknown, once the counts show any mail at all.
 *
 * It carries no `emailEntry` and no `route` on purpose: opening a Briefing ENTRY (attention,
 * today, …) lands on that entry's list, and this row is the way to the WHOLE Briefing — the modal,
 * which opens on the dashboard and lets an entry be chosen inside it.
 */
export function emailBriefingRow(counts, { connected = false } = {}) {
  const total = countValue(counts, ...TOTAL_FIELDS)
  const attention = countValue(counts, ...ATTENTION_FIELDS)
  if (!connected && total <= 0 && attention <= 0) return null
  return {
    section: EMAIL_BRIEFING_SECTION,
    label: emailBriefingLabel(),
    count: attention,
    rank: 0,
    kind: 'email',
    modal: EMAIL_BRIEFING_MODAL,
  }
}

/**
 * The three Ongoing cases home draws, in ladder order (busy-timeline-srv#27) — each is one status
 * key from `GET v1/ongoing`, its own section name, and the words the row says.
 *
 * `needsAttention` first because it is the user owing an answer, `overdue` next because it is
 * already late, `waiting` last because the next move belongs to somebody else. The ranks continue
 * the email row's (0), so one sort orders the whole strip.
 *
 * The label is a function of the count so singulars read as sentences ("1 ongoing needs
 * attention", not "1 ongoings need"). Data, not a switch, so a fourth rung is one line here.
 */
export const ONGOING_CASES = [
  {
    status: 'needsAttention',
    section: 'ongoingAttention',
    rank: 1,
    // "17 ongoings need attention" — Figma 40008539:200787 / m01 (Pouria 2026-10-15) drop the
    // "your" this row used to carry.
    label: n => `${n} ongoing${n === 1 ? '' : 's'} need${n === 1 ? 's' : ''} attention`,
  },
  {
    status: 'overdue',
    section: 'ongoingOverdue',
    rank: 2,
    label: n => `${n} ongoing${n === 1 ? '' : 's'} overdue`,
  },
  {
    status: 'waiting',
    section: 'ongoingWaiting',
    rank: 3,
    // "Waiting on someone", in the reporter's own vocabulary (busy-my-web#374, Saffet: "*Waiting on
    // someone* = no action from me yet; I'm waiting"). It used to read "waiting on a reply" /
    // "waiting on replies", which is narrower than the rung actually is — a wait can be on a
    // delivery, a decision or a person, and only sometimes on an email coming back. The narrow
    // wording was also a leftover of the thread-based count busy-my-web#336 replaced.
    label: n => `${n} ongoing${n === 1 ? '' : 's'} waiting on someone`,
  },
]

/**
 * The ladder rung a row is on, in the one spelling this file compares against (busy-my-web#374).
 *
 * The BFF passes `status` through untouched and says so — "the BFF does not own this vocabulary and
 * must not narrow it" — so what reaches here is whatever timeline-srv serialized, and a rung that
 * arrives as "Waiting" or with a stray space is a rung this strip silently has no row for. Saffet's
 * report is a missing row, so the comparison must not be able to miss one for a reason nobody can
 * see. Case and surrounding space are not meaning; anything else still is.
 */
export function statusRung(status) {
  return String(status ?? '').trim().toLowerCase()
}

/**
 * Which ongoings a home count is allowed to include.
 *
 * TOP-LEVEL ONLY, and nothing archived or still a suggestion. A sub-ongoing's state is already
 * rolled up into its parent's status by the server (the most urgent rung in the subtree wins), so
 * counting children as well would count one late thing twice; an archived story is filed away; and
 * a `isSuggested` row is a question the app has not asked yet, not an ongoing the user has.
 */
export function countableOngoings(items) {
  return (Array.isArray(items) ? items : [])
    .filter(o => o && o.id && !o.parentOngoingId && !o.isArchived && !o.isSuggested)
}

/**
 * The ongoings somebody else owes something on, from the WAITING ROWS rather than the ladder
 * (busy-my-web#374, second fix).
 *
 * The `waiting` rung is a property of an ongoing's status, and the server rarely puts an ongoing on
 * it — so the row Saffet reported missing stayed missing even after the label was fixed. The waits
 * themselves are a list of their own (`GET v1/waiting?state=open`, the module in store/modules/
 * waiting.js), and since busy-main-bff 21c4147 every row on it carries the ongoing it belongs to.
 *
 * The count is DISTINCT ongoings, not waits: three chases on one house purchase are one ongoing
 * waiting on someone, and the row says "1 ongoing", because the row opens ongoings. Ids are
 * compared case-insensitively (a GUID is the same GUID in either case) but kept in the spelling
 * that arrived, since that spelling is what `/ongoing/:id` is built from.
 *
 * Rows with no `ongoingId` are UNASSIGNED waits — a reply owed on a thread that belongs to no
 * story. They are real waits and the Waiting list keeps drawing them; they are simply not an
 * ongoing, so they cannot be counted as one here.
 */
export function waitingOngoingIds(waitingRows) {
  const seen = new Set()
  const ids = []
  for (const row of Array.isArray(waitingRows) ? waitingRows : []) {
    if (!row || row.resolvedUtc) continue
    const id = String(row.ongoingId ?? '').trim()
    if (!id) continue
    const key = id.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    ids.push(id)
  }
  return ids
}

/**
 * The waiting row's ongoings, in the shape every other row carries them: `{ id, title }`.
 *
 * A wait can point at an ongoing this list does not hold — a sub-ongoing, an archived one, or one
 * whose page simply has not been fetched — and that is still an ongoing being waited on, so it is
 * counted and offered; it just arrives without a title, which the opened list already handles
 * ("Ongoing", lib/homeOngoingList.js).
 */
function waitingOngoings(ids, items) {
  const byId = new Map()
  for (const o of Array.isArray(items) ? items : []) {
    if (o && o.id) byId.set(String(o.id).toLowerCase(), o)
  }
  return ids.map(id => ({ id, title: String(byId.get(id.toLowerCase())?.title || '').trim() }))
}

/**
 * The Ongoing rows: one per case that has at least one ongoing on it, zero counts simply absent
 * (the block's existing rule — a row that opens an empty list is not a follow-up).
 *
 * Each row carries the ongoings it counted, so opening it can go straight to the single one it
 * means, or open the list of all it means in the middle of Home (busy-my-web#630,
 * lib/homeOngoingList.js). No new page and no new filter: every row of that list is an Ongoing's own
 * story, `/ongoing/:id`.
 *
 * `waitingRows` is the open waiting list when it is in hand, and `null` when it is not. Not in hand
 * means the fetch is still in flight, or failed, or the endpoint is not there — none of which is
 * "nothing is being waited on", so the waiting row falls back to the ladder count it was computed
 * from before (busy-my-web#336). Nothing regresses on a slow or broken call; it only improves on a
 * good one.
 *
 * @param items the ongoing list (`ongoing/items`)
 * @param waitingRows the open waits (`waiting/rows`), or null when they have not loaded
 */
export function ongoingRows(items, waitingRows = null) {
  const list = countableOngoings(items)
  const haveWaits = Array.isArray(waitingRows)
  return ONGOING_CASES
    .map((c) => {
      const fromWaits = haveWaits && c.status === 'waiting'
      const matched = fromWaits
        ? waitingOngoings(waitingOngoingIds(waitingRows), items)
        : list.filter(o => statusRung(o.status) === statusRung(c.status))
      if (!matched.length) return null
      return {
        section: c.section,
        label: c.label(matched.length),
        count: matched.length,
        rank: c.rank,
        kind: 'ongoing',
        status: c.status,
        ongoings: fromWaits
          ? matched
          : matched.map(o => ({ id: String(o.id), title: String(o.title || '').trim() })),
      }
    })
    .filter(Boolean)
}

/**
 * A list with every ongoing in it at most once, the first of each id kept (busy-my-web#571: two
 * identical "Social media" rows under one count). Ids compare as strings, case aside — the waits
 * hand them back lower-cased. Entries with no id are left alone; the callers drop those themselves.
 */
export function uniqueById(items) {
  const seen = new Set()
  return (Array.isArray(items) ? items : []).filter((o) => {
    if (!o || !o.id) return true
    const key = String(o.id).toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

// Gone (busy-my-web#630, Pouria 2026-10-14): `ongoingGroupRows`, the in-strip unfold — "All
// follow-ups" over three of the counted stories. A count of several now OPENS a list instead: the
// strip steps aside, the box is headed by a Briefing about the list, and every story is listed in
// the middle of Home (lib/homeOngoingList.js, `briefing/openOngoingList`).

/**
 * ONE SOURCE'S ROWS, and only that source's (busy-my-web#531).
 *
 * The strip's legs are separate calls with separate failure modes, so they are read separately: a
 * mailbox answer of a shape this file has never seen — or an ongoing list holding something that is
 * not an ongoing — costs its OWN rows and nothing else's. Before this the rows were built in one
 * expression, where anything that threw left home with no Follow-ups at all, which is the report
 * this issue is about and the one state the strip must never reach silently.
 *
 * A source may answer one row, a list of them, or nothing; all three come back as a list.
 */
function fromSource(build) {
  try {
    const out = build()
    if (Array.isArray(out)) return out.filter(Boolean)
    return out ? [out] : []
  } catch {
    return []
  }
}

/**
 * The rows home draws: the email row first, then the Ongoing cases in rank order, capped at four.
 *
 * @param counts the Briefing's counts (`GET v1/email/briefing`) — the one email row
 * @param ongoings the ongoing list (`ongoing/items`) — every other row
 * @param waitingRows the open waits (`waiting/rows`) or null — what the waiting row is counted from
 *
 * The ongoing list is deduped by id before anything is counted (busy-my-web#571), so an ongoing
 * listed twice is one ongoing, not two.
 */
export function homeFollowUpRows({ counts = null, ongoings = [], waitingRows = null, connected = false, max = MAX_ROWS } = {}) {
  return [...fromSource(() => emailBriefingRow(counts, { connected })), ...fromSource(() => ongoingRows(uniqueById(ongoings), waitingRows))]
    .filter(Boolean)
    .map((row, i) => ({ row, i }))
    .sort((a, b) => (a.row.rank - b.row.rank) || (a.i - b.i))
    .map(({ row }) => row)
    .slice(0, max)
}

// ── THE CHAT'S "Create…" ROW (chat Figma rebuild, WP1; frames 01–04, M01, GAPS F3) ──────────────
//
// Under a Global conversation answer the frames list the model's next questions and, as the last
// line, "Create… Task · Note · Event · Contact" — each word a way straight into that creation form
// in the box (the `smartbox-edit-entity` event every other "open this in the box" gesture sends).
// It is offered while there is ROOM for it: frames 12/13/16/17 fill all four lines with questions
// and draw no Create row, so it only joins three follow-ups or fewer. Pure, like the rest of this
// file, so the rule is tested without mounting the strip.

/** The four words, in the frames' order, and the box mode each one opens. */
export const CREATE_TARGETS = [
  { label: 'Task', menu: 'tasks' },
  { label: 'Note', menu: 'notes' },
  { label: 'Event', menu: 'events' },
  { label: 'Contact', menu: 'contacts' },
]

/** The section key the Create row is drawn under. */
export const CHAT_CREATE_SECTION = 'chat-create'

/**
 * The chat's rows with the Create row appended when there is room for it.
 * @param {Array<object>} rows  the chat follow-up rows, already built
 * @param {number} [max]        the strip's line limit
 */
export function withCreateRow(rows, max = MAX_ROWS) {
  const list = Array.isArray(rows) ? rows : []
  if (list.length >= max) return list
  return [...list, { section: CHAT_CREATE_SECTION, label: 'Create…', create: true, targets: CREATE_TARGETS }]
}

/**
 * Home's rows with the Create row ALWAYS closing them (busy-my-web#582, Pouria 2026-10-07: "we need
 * this create task note event in home as well"). Plain Home — no conversation yet — is the page
 * most people land on, and the four creation shortcuts were only ever drawn under a chat answer.
 * Unlike the chat's `withCreateRow`, this one does not ask for room: on Home the row is a way to
 * MAKE things, not a follow-up, so it stands outside the four-line follow-up cap (the caller caps
 * the rows first; this appends the fifth line at most). The chat frames' "no Create row when four
 * questions fill the strip" rule is the chat's alone and is untouched.
 * @param {Array<object>} rows  the home follow-up rows, already capped
 */
export function withHomeCreateRow(rows) {
  const list = Array.isArray(rows) ? rows : []
  if (list.some(r => r?.create)) return list
  return [...list, { section: CHAT_CREATE_SECTION, label: 'Create…', create: true, targets: CREATE_TARGETS }]
}

/** The detail `smartbox-edit-entity` needs to open one Create word's form, empty. */
export function createTargetDetail(target) {
  return { menu: target?.menu || 'tasks', title: '' }
}

// ── THE CREATE ROW FOLLOWS THE BOX (busy-my-web#622) ─────────────────────────────────────────────
//
// Saffet: "can we make it change as you move along" — a creation opened from "Create… Task · Note ·
// Event · Contact" can be switched to another kind in the box's pencil (task → note → event …), and
// the line it was opened from kept saying nothing about which one is now open. Pouria, 2026-10-20:
// the line follows the kind LIVE — while a creation is open in the box, the word for the kind being
// written is the selected one, the highlight moves when the pencil switches kind, and pressing
// another word switches the box rather than opening a second form.
//
// One source for "what is being created": the session the box already publishes (store
// `focusedSession`, busy-my-web#255). Nothing here keeps a copy of the kind.

/**
 * The kind the box is CREATING right now — its quick mode ('tasks' | 'notes' | …) — or null.
 *
 * Only a creation in the box itself counts: an existing entity open in it (`editing`) is not one,
 * nor the full creation form (`formMode`), a reply (`replying`) or a search selection
 * (`selecting`). Those keep #255's rule whole — the strip stands down — and none of them is
 * something the Create row opened or can switch.
 * @param {object|null} session  `focusedSession/session`
 */
export function boxCreationKind(session) {
  const s = session || {}
  if (!s.quickMode || s.editing || s.formMode || s.replying || s.selecting) return null
  return s.quickMode
}

/**
 * What pressing one Create word does, given the kind the box is creating (busy-my-web#622):
 *   'open'   — nothing is being created: open that form, empty (`smartbox-edit-entity`, as before)
 *   'switch' — another kind is open: switch the box to this one, as picking it in the pencil does
 *              (the draft's title and body travel with it — `setMode` keeps them)
 *   'keep'   — this kind is already open: nothing. Unlike the pencil's own row, where picking the
 *              selected module is the way OUT of it (#203), the word here is a shortcut INTO a
 *              form — a second press must not throw a half-written draft away.
 * @param {{ menu: string }} target  one of CREATE_TARGETS
 * @param {string|null} kind         `boxCreationKind(...)`
 */
export function createWordAction(target, kind) {
  if (!kind) return 'open'
  return target?.menu === kind ? 'keep' : 'switch'
}

/** The window event the Create row sends to switch the open creation (SmartBox listens). */
export const SWITCH_CREATE_EVENT = 'smartbox-switch-create'
